/**
 * JourneyContext — estado da Jornada de 6 fases.
 *
 * Gerencia o projeto ativo (projectId persistido na sessão via sessionStorage),
 * o tipo de projeto (brownfield/greenfield), a etapa atual e a mais avançada.
 *
 * Por que sessionStorage: o projectId deve sobreviver a um F5 (refresh de aba)
 * mas não a fechar a aba — igual ao refresh_token no AuthContext.
 *
 * A lógica de etapas espelha o protótipo:
 *   JOURNEY_STEPS_BROWNFIELD: project → git → … → blueprint → artifacts →
 *                             components → techCriteria → gateTecnologia → backlog
 *   JOURNEY_STEPS_GREENFIELD: idem + ideation (após import)
 *
 * Neste lote (1), as etapas import e ideation são navegáveis mas exibem
 * placeholder — o lote 2 as liga à API real.
 *
 * Clean: sem lógica de UI; só estado + ações.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useEffect,
} from 'react';

// ─── Etapas disponíveis ───────────────────────────────────────────────────────

/** Etapas na ordem exata do wizard. */
export const JOURNEY_STEPS_BROWNFIELD = [
  'project',
  'git',
  'graph',        // análise do repositório (grafo Graphify) — revisável a qualquer momento
  'import',
  'context',        // Contexto & Discovery — inclui as Fontes de Negócio (ex-dataBiz)
  'designStudio',
  'gateNegocio',   // Quality Gate 1 · Negócio — aprovação humana ao fim da Fase 2
  'aderencia',     // Aderência ao Escopo (o que pediu × o que geramos) — pós-Gate 1
  'blueprint',
  'artifacts',
  'components',
  'techCriteria',
  'gateTecnologia',  // Quality Gate 2 · Tecnologia — aprovação humana ao fim da Fase 3
  'squad',           // Fase 4 · Definição do Squad (composição a nível de projeto)
  'gateSquads',      // Quality Gate 3 · Squads — aprovação humana ao fim da Fase 4
  'backlog',
  'cycles',
  'gateEscopo',      // Quality Gate 4 · Escopo — aprovação humana ao fim da Fase 5
];

export const JOURNEY_STEPS_GREENFIELD = [
  'project',
  'git',
  'graph',        // análise do repositório (grafo Graphify) — revisável a qualquer momento
  'import',
  'context',      // Contexto & Discovery (escopo + notas + docs + Fontes de Negócio)
  'ideation',     // ideação assistida por IA usa o escopo subido acima
  'designStudio',
  'gateNegocio',   // Quality Gate 1 · Negócio — aprovação humana ao fim da Fase 2
  'aderencia',     // Aderência ao Escopo (o que pediu × o que geramos) — pós-Gate 1
  'blueprint',
  'artifacts',
  'components',
  'techCriteria',
  'gateTecnologia',  // Quality Gate 2 · Tecnologia — aprovação humana ao fim da Fase 3
  'squad',           // Fase 4 · Definição do Squad (composição a nível de projeto)
  'gateSquads',      // Quality Gate 3 · Squads — aprovação humana ao fim da Fase 4
  'backlog',
  'cycles',
  'gateEscopo',      // Quality Gate 4 · Escopo — aprovação humana ao fim da Fase 5
];

/** Metadados de cada etapa: label, fase. */
export const STEP_META = {
  project:      { label: 'Tipo do Projeto',         phase: 'definicoes'  },
  git:          { label: 'Repositório Git',          phase: 'definicoes'  },
  graph:        { label: 'Análise do Repositório',   phase: 'definicoes'  },
  import:       { label: 'Importação',               phase: 'negocio'     },
  ideation:     { label: 'Ideação',                  phase: 'negocio'     },
  context:      { label: 'Contexto & Discovery',     phase: 'negocio'     },
  dataBiz:      { label: 'Data Sources (Negócio)',   phase: 'negocio'     },
  designStudio: { label: 'Design Studio',            phase: 'negocio'     },
  gateNegocio:  { label: 'Gate 1 · Negócio',         phase: 'negocio'     },
  aderencia:    { label: 'Aderência ao Escopo',      phase: 'negocio'     },
  blueprint:    { label: 'Blueprint',                phase: 'tecnologia'  },
  artifacts:    { label: 'Artefatos',                phase: 'tecnologia'  },
  components:   { label: 'Biblioteca de Componentes', phase: 'tecnologia' },
  techCriteria: { label: 'Critérios Técnicos',       phase: 'tecnologia'  },
  gateTecnologia: { label: 'Gate 2 · Tecnologia',    phase: 'tecnologia'  },
  squad:        { label: 'Squad Designer',           phase: 'squads'      },
  gateSquads:   { label: 'Gate 3 · Squads',          phase: 'squads'      },
  backlog:      { label: 'Product Backlog',          phase: 'ciclos'      },
  cycles:       { label: 'Ciclos de Desenvolvimento', phase: 'ciclos'     },
  gateEscopo:   { label: 'Gate 4 · Escopo',          phase: 'ciclos'      },
};

/** As 6 fases com labels. */
export const PHASES = [
  { id: 'definicoes',  label: 'Definições Iniciais' },
  { id: 'negocio',     label: 'Escopo de Negócio' },
  { id: 'tecnologia',  label: 'Escopo de Tecnologia' },
  { id: 'squads',      label: 'Definição dos Squads' },
  { id: 'ciclos',      label: 'Ciclos de Desenvolvimento' },
  { id: 'execucao',    label: 'Execução & Revisão' },
];

// ─── Estado inicial ───────────────────────────────────────────────────────────

const STORAGE_KEY = 'nava:journey';

/**
 * clearJourneyState — apaga o estado da jornada persistido na sessão.
 *
 * Usado por quem inicia uma jornada NOVA a partir de FORA do JourneyProvider
 * (ex.: botão "Novo projeto" no Dashboard). Sem isto, ao navegar para /journey
 * o initialState relê o sessionStorage e RESTAURA o projeto anterior — a tela
 * abre no meio (ex.: Definição de Squads) "como se estivesse parado numa
 * execução antiga". Chamar antes de navigate('journey') garante começo limpo.
 * O provider remonta na rota nova e o initialState já lê o storage vazio.
 */
export function clearJourneyState() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* sem storage — ignora */
  }
}

function loadFromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveToStorage(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      projectId:     state.projectId,
      projectType:   state.projectType,
      deliveryScope: state.deliveryScope,
      current:       state.current,
      furthest:      state.furthest,
    }));
  } catch { /* sem storage — ignora */ }
}

function initialState(override = null) {
  const stored = override ?? loadFromStorage();
  return {
    projectId:     stored?.projectId     ?? null,
    projectType:   stored?.projectType   ?? 'brownfield',
    deliveryScope: stored?.deliveryScope ?? 'fullstack',
    current:       stored?.current       ?? 'project',
    furthest:      stored?.furthest       ?? 'project',
  };
}

/**
 * Estado inicial SEMEADO pelo deep-link da Gestão do Projeto
 * (/journey?project=&step=&type=&scope=). Roda no init do useReducer — ANTES do
 * 1º render dos steps — para o `projectId` já estar presente na montagem. Sem
 * isto, o `openAt` só aplicava num useEffect (pós-render) e os steps montavam em
 * modo "novo projeto", perdendo os dados salvos do projeto existente.
 */
function initialStateFromDeepLink(dl) {
  const proj = dl?.project;
  if (!proj) return initialState();          // sem deep-link → comportamento normal
  const stored = loadFromStorage();
  const sameProject = stored?.projectId === proj;
  const projectType   = dl.type  || (sameProject ? stored?.projectType   : null) || 'brownfield';
  const deliveryScope = dl.scope || (sameProject ? stored?.deliveryScope : null) || 'fullstack';
  const steps = stepsFor(projectType, deliveryScope);
  const wanted = dl.step || (sameProject ? stored?.current : null);
  const current = wanted && steps.includes(wanted) ? wanted : steps[0];
  return {
    projectId: proj,
    projectType,
    deliveryScope,
    current,
    furthest: steps[steps.length - 1] ?? current,  // review: navega livre em todos os steps
  };
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

function journeyReducer(state, action) {
  switch (action.type) {
    case 'SET_PROJECT_ID':
      return { ...state, projectId: action.payload };

    case 'SET_PROJECT_TYPE': {
      const newType = action.payload;
      const steps   = stepsFor(newType, state.deliveryScope);
      // Se o current/furthest não existem na nova lista, reclampa para project
      const newCurrent  = steps.includes(state.current)  ? state.current  : 'project';
      const newFurthest = steps.includes(state.furthest) ? state.furthest : 'project';
      return { ...state, projectType: newType, current: newCurrent, furthest: newFurthest };
    }

    case 'SET_DELIVERY_SCOPE': {
      const newScope = action.payload;
      const steps    = stepsFor(state.projectType, newScope);
      // Ao trocar o escopo, etapas de UI podem sair/entrar; reclampa se preciso.
      const newCurrent  = steps.includes(state.current)  ? state.current  : 'project';
      const newFurthest = steps.includes(state.furthest) ? state.furthest : 'project';
      return { ...state, deliveryScope: newScope, current: newCurrent, furthest: newFurthest };
    }

    case 'GO_TO_STEP': {
      const step  = action.payload;
      const steps = stepsFor(state.projectType, state.deliveryScope);
      const idx   = steps.indexOf(step);
      const fIdx  = steps.indexOf(state.furthest);
      // Só permite ir para etapas já visitadas (idx <= fIdx) — avança bloqueado
      if (idx < 0 || idx > fIdx) return state;
      return { ...state, current: step };
    }

    case 'COMPLETE_STEP': {
      const steps    = stepsFor(state.projectType, state.deliveryScope);
      const currIdx  = steps.indexOf(state.current);
      const nextStep = steps[currIdx + 1] ?? 'done';
      const fIdx     = steps.indexOf(state.furthest);
      const newFurthest = currIdx + 1 > fIdx ? (nextStep !== 'done' ? nextStep : state.furthest) : state.furthest;
      return { ...state, current: nextStep, furthest: newFurthest };
    }

    // Abre a jornada de um projeto EXISTENTE num passo específico (deep-link da
    // Gestão do Projeto). Como o projeto já existe e tem conteúdo salvo em cada
    // etapa, TUDO fica navegável para revisão (furthest = último passo) — o usuário
    // pode olhar qualquer tela e voltar livremente (ex.: Design Studio → Ideação).
    case 'OPEN_AT': {
      const { projectId, projectType, deliveryScope, step } = action.payload || {};
      const pt = projectType || state.projectType;
      const ds = deliveryScope || state.deliveryScope;
      const steps = stepsFor(pt, ds);
      const cur = step && steps.includes(step) ? step : steps[0];
      const last = steps[steps.length - 1] ?? cur;
      return {
        ...state,
        projectId: projectId ?? state.projectId,
        projectType: pt,
        deliveryScope: ds,
        current: cur,
        furthest: last,
      };
    }

    case 'RESET':
      return initialState(null);

    default:
      return state;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Etapas que só fazem sentido quando o projeto entrega UI (telas).
 * Espelha a lógica do pipeline (#6): `_has_ui = delivery_scope ∈ {fullstack, frontend}`.
 * Em 'analise' e 'backend' (sem UI), o Design Studio (geração de telas) é omitido —
 * assim a Jornada concorda com o que a execução realmente gera.
 */
const UI_ONLY_STEPS = new Set(['designStudio', 'aderencia']);

/**
 * Etapas da FASE TECNOLOGIA — puladas em 'analise' (uma análise não escolhe stack,
 * blueprint nem artefatos técnicos). O Gate 2 · Tecnologia é AUTO-APROVADO no back
 * para análise (ver journey_gates). Design Studio já é omitido por UI_ONLY.
 */
const TECH_ONLY_STEPS = new Set([
  'blueprint', 'artifacts', 'components', 'techCriteria', 'gateTecnologia',
]);

/**
 * Etapas da FASE CICLOS (Fase 5) — puladas em 'analise'. Backlog/Ciclos/Gate 4·Escopo
 * só fazem sentido para projetos que GERAM CÓDIGO: o pipeline PULA o codegen em
 * análise (`_produces_code=False`), então os ciclos planejados NUNCA são executados
 * (o usuário definia 3 ciclos e a execução entregava um único relatório). Removendo
 * aqui, a Jornada concorda com o que a execução realmente faz.
 * DECISÃO DO DONO (2026-08-31): reverte a de 2026-08-19 — análise NÃO oferece mais
 * backlog/ciclos. O Squad (Fase 4) segue (define QUAIS analistas), mas a entrega da
 * análise é o relatório, aprovado no Gate B de EXECUÇÃO ("Revisão da Análise").
 */
const CICLOS_STEPS = new Set(['backlog', 'cycles', 'gateEscopo']);

function scopeHasUi(deliveryScope) {
  return deliveryScope === 'fullstack' || deliveryScope === 'frontend';
}

export function stepsFor(projectType, deliveryScope = 'fullstack') {
  const base = projectType === 'greenfield'
    ? JOURNEY_STEPS_GREENFIELD
    : JOURNEY_STEPS_BROWNFIELD;
  let steps = base;
  if (!scopeHasUi(deliveryScope)) {
    steps = steps.filter((step) => !UI_ONLY_STEPS.has(step));
  }
  if (deliveryScope === 'analise') {
    // Análise: pula a fase de Tecnologia (Gate 2 auto-aprovado no back) e a fase de
    // Ciclos (backlog/ciclos/Gate 4 não executam em análise). Mantém o Squad (Fase 4).
    steps = steps.filter((step) => !TECH_ONLY_STEPS.has(step) && !CICLOS_STEPS.has(step));
  }
  return steps;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const JourneyContext = createContext(null);

/** Lê o deep-link da URL atual SEM depender do Router (não quebra testes sem
 *  <Router>). Roda uma vez no init do useReducer. */
function readDeepLinkFromLocation() {
  try {
    const sp = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : '',
    );
    return {
      project: sp.get('project'),
      step: sp.get('step'),
      type: sp.get('type'),
      scope: sp.get('scope'),
    };
  } catch {
    return {};
  }
}

export function JourneyProvider({ children }) {
  // Semeia o estado inicial pelo deep-link da URL (project/step/type/scope), para
  // o projeto existente já estar hidratado na 1ª renderização dos steps.
  const [state, dispatch] = useReducer(
    journeyReducer,
    undefined,
    () => initialStateFromDeepLink(readDeepLinkFromLocation()),
  );

  // Persiste no sessionStorage sempre que o estado muda
  useEffect(() => {
    saveToStorage(state);
  }, [state]);

  const setProjectId = useCallback(
    (id) => dispatch({ type: 'SET_PROJECT_ID', payload: id }),
    [],
  );

  const setProjectType = useCallback(
    (type) => dispatch({ type: 'SET_PROJECT_TYPE', payload: type }),
    [],
  );

  const setDeliveryScope = useCallback(
    (scope) => dispatch({ type: 'SET_DELIVERY_SCOPE', payload: scope }),
    [],
  );

  const goToStep = useCallback(
    (step) => dispatch({ type: 'GO_TO_STEP', payload: step }),
    [],
  );

  // Abre um projeto existente num passo (deep-link da Gestão do Projeto).
  const openAt = useCallback(
    (payload) => dispatch({ type: 'OPEN_AT', payload }),
    [],
  );

  const completeStep = useCallback(
    () => dispatch({ type: 'COMPLETE_STEP' }),
    [],
  );

  const reset = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    dispatch({ type: 'RESET' });
  }, []);

  const steps   = stepsFor(state.projectType, state.deliveryScope);
  const currIdx = steps.indexOf(state.current);
  const fIdx    = steps.indexOf(state.furthest);

  /**
   * Retorna true se `step` pode ser clicado no stepper:
   *   - Qualquer etapa visitada (idx <= fIdx) pode ser navegada livremente.
   *   - Avançar além de `furthest` é bloqueado.
   */
  const canNavigateTo = useCallback(
    (step) => {
      const idx = steps.indexOf(step);
      return idx >= 0 && idx <= fIdx;
    },
    [steps, fIdx],
  );

  const value = {
    // Estado
    projectId:     state.projectId,
    projectType:   state.projectType,
    deliveryScope: state.deliveryScope,
    current:       state.current,
    furthest:      state.furthest,
    steps,
    currentIndex: currIdx,
    furthestIndex: fIdx,
    isDone: state.current === 'done',

    // Ações
    setProjectId,
    setProjectType,
    setDeliveryScope,
    goToStep,
    openAt,
    completeStep,
    reset,
    canNavigateTo,
  };

  return (
    <JourneyContext.Provider value={value}>
      {children}
    </JourneyContext.Provider>
  );
}

export function useJourney() {
  const ctx = useContext(JourneyContext);
  if (!ctx) {
    throw new Error('useJourney deve ser usado dentro de <JourneyProvider>');
  }
  return ctx;
}
